import { useState, useEffect } from "react"
import { useNavigate, useSearchParams, Link } from "react-router-dom"
import { useAuth as useClerkAuth } from "@clerk/react"
import { useSignIn, useSignUp } from "@clerk/react/legacy"
import { isDoctorHost } from "@/lib/host"
import { sanitizeNextPath } from "@/lib/safeRedirect"

type Role = "patient" | "doctor"
type Step = "email" | "code"

const ROLE_KEY = "d2c_signup_role"

export default function LoginPage() {
  const navigate    = useNavigate()
  const [params]    = useSearchParams()
  const { isSignedIn, isLoaded } = useClerkAuth()
  const { signIn, setActive: setActiveSignIn, isLoaded: signInLoaded } = useSignIn()
  const { signUp, setActive: setActiveSignUp, isLoaded: signUpLoaded } = useSignUp()

  const [role, setRole] = useState<Role | null>(() => {
    try { return (localStorage.getItem(ROLE_KEY) as Role | null) } catch { return null }
  })

  const [step,      setStep]      = useState<Step>("email")
  const [email,     setEmail]     = useState("")
  const [code,      setCode]      = useState("")
  const [isNewUser, setIsNewUser] = useState(false)
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState<string | null>(null)
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  const isDoctorSurface = true
  const nextParam = params.get("next")
  const safeNext = sanitizeNextPath(nextParam, "")
  const afterAuth = safeNext ? `/app/auth/callback?next=${encodeURIComponent(safeNext)}` : "/app/auth/callback"

  // Complete the Clerk OAuth "transfer" flow for users returning from Google.
  // When a brand-new user authenticates with Google, Clerk has no account yet,
  // so it redirects to the configured sign-up URL (/login) with a *transferable*
  // sign-up (or sign-in) resource. This must be explicitly completed with
  // { transfer: true } to create the account/session — it does NOT depend on
  // URL params, only on Clerk's in-memory verification state.
  useEffect(() => {
    if (!signInLoaded || !signUpLoaded || !signIn || !signUp) return
    if (isSignedIn) return
    const dest = afterAuth

    // NEW user: the sign-IN came back transferable (no account exists yet) →
    // transfer it into a sign-UP to create the account.
    if (signIn.firstFactorVerification?.status === "transferable") {
      signUp.create({ transfer: true })
        .then(async (res) => {
          if (res.status === "complete" && res.createdSessionId) {
            await setActiveSignUp!({ session: res.createdSessionId })
            navigate(dest, { replace: true })
          } else {
            console.error("[login] OAuth sign-up transfer incomplete:", res.status)
          }
        })
        .catch((e: any) => console.error("[login] OAuth sign-up transfer failed:", e))
      return
    }

    // EXISTING user: the sign-UP came back with a transferable external account
    // (account already exists) → transfer it into a sign-IN.
    if (signUp.verifications?.externalAccount?.status === "transferable") {
      signIn.create({ transfer: true })
        .then(async (res) => {
          if (res.status === "complete" && res.createdSessionId) {
            await setActiveSignIn!({ session: res.createdSessionId })
            navigate(dest, { replace: true })
          } else {
            console.error("[login] OAuth sign-in transfer incomplete:", res.status)
          }
        })
        .catch((e: any) => console.error("[login] OAuth sign-in transfer failed:", e))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signInLoaded, signUpLoaded, isSignedIn])

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return
    navigate(afterAuth, { replace: true })
  }, [isLoaded, isSignedIn, navigate, afterAuth])

  // ── D2C role picker ───────────────────────────────────────────────────────
  // Rendered before the Clerk loading gate: it's pure UI (localStorage only),
  // so it must not hang on Clerk. If Clerk can't initialize (e.g. origin not
  // allowed on a non-prod host), the picker still works.
  if (!role) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Welcome to SummariseNOW</h1>
            <p className="mt-2 text-sm text-gray-500">How are you using this app?</p>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => {
                try { localStorage.setItem(ROLE_KEY, "doctor") } catch {}
                setRole("doctor")
              }}
              className="w-full rounded-xl border border-gray-200 bg-white px-5 py-4 text-left hover:border-indigo-300 hover:shadow-sm transition-all"
            >
              <div className="font-medium text-gray-900">For Doctors &amp; Clinics</div>
              <div className="mt-0.5 text-sm text-gray-500">View patient summaries, knowledge search, cloud inbox</div>
            </button>
            <button
              onClick={() => {
                try { localStorage.setItem(ROLE_KEY, "patient") } catch {}
                setRole("patient")
              }}
              className="w-full rounded-xl border border-gray-200 bg-white px-5 py-4 text-left hover:border-indigo-300 hover:shadow-sm transition-all"
            >
              <div className="font-medium text-gray-900">For Patients &amp; Families</div>
              <div className="mt-0.5 text-sm text-gray-500">Upload records, get summaries, share with your doctor</div>
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!isLoaded || !signInLoaded || !signUpLoaded) {
    return <div className="flex h-screen items-center justify-center text-gray-400 text-sm">Loading…</div>
  }

  // ── Custom email → OTP form (no Clerk branding) ───────────────────────────
  async function handleGoogleSignIn() {
    if (!signIn) return
    if (isDoctorSurface && !acceptedTerms) {
      setError("Please accept the Terms and Privacy Policy to continue.")
      return
    }
    setLoading(true)
    setError(null)
    try {
      await signIn.authenticateWithRedirect({
        strategy:            "oauth_google",
        redirectUrl:         `${window.location.origin}/app/auth/callback`,
        redirectUrlComplete: afterAuth,
      })
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage ?? "Google sign-in failed. Try again.")
      setLoading(false)
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!signIn || !signUp) return
    if (isDoctorSurface && !acceptedTerms) {
      setError("Please accept the Terms and Privacy Policy to continue.")
      return
    }
    setLoading(true)
    setError(null)
    try {
      // Try sign-in first
      await signIn.create({ identifier: email, strategy: "email_code" })
      setIsNewUser(false)
      setStep("code")
    } catch (err: any) {
      const errCode = err?.errors?.[0]?.code ?? ""
      if (errCode === "form_identifier_not_found" || errCode === "form_password_incorrect") {
        // New user — start sign-up
        try {
          await signUp.create({ emailAddress: email })
          await signUp.prepareEmailAddressVerification({ strategy: "email_code" })
          setIsNewUser(true)
          setStep("code")
        } catch (e2: any) {
          setError(e2?.errors?.[0]?.longMessage ?? e2?.errors?.[0]?.message ?? "Could not send code. Try again.")
        }
      } else {
        setError(err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? "Could not send code. Try again.")
      }
    }
    setLoading(false)
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      if (isNewUser) {
        const result = await signUp!.attemptEmailAddressVerification({ code })
        if (result.status === "complete") {
          await setActiveSignUp!({ session: result.createdSessionId })
          navigate(afterAuth, { replace: true })
        } else {
          setError("Verification incomplete. Please try again.")
        }
      } else {
        const result = await signIn!.attemptFirstFactor({ strategy: "email_code", code })
        if (result.status === "complete") {
          await setActiveSignIn!({ session: result.createdSessionId })
          navigate(afterAuth, { replace: true })
        } else {
          setError("Sign-in incomplete. Please try again.")
        }
      }
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? "Invalid code. Try again.")
    }
    setLoading(false)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      {isDoctorSurface && role && step === "email" && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2">
          <button
            onClick={() => {
              try { localStorage.removeItem(ROLE_KEY) } catch {}
              setRole(null)
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ← Change role
          </button>
        </div>
      )}

      <div className="w-full max-w-sm">
        <div className="rounded-2xl border border-gray-200 bg-white px-8 py-10 shadow-sm">

          {step === "email" ? (
            <>
              <div className="mb-7 text-center">
                <h1 className="text-xl font-semibold text-gray-900">Sign in</h1>
                <p className="mt-1.5 text-sm text-gray-500">
                  {isDoctorSurface
                    ? role === "doctor" ? "Sign in as a doctor or clinic" : "Sign in as a patient"
                    : "Sign in to your account"}
                </p>
              </div>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                {isDoctorSurface && (
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => { setAcceptedTerms(e.target.checked); if (e.target.checked) setError(null) }}
                      className="mt-0.5"
                    />
                    <span className="text-xs leading-relaxed text-gray-600">
                      I agree to the{" "}
                      <Link to="/terms" className="font-medium text-gray-800 underline">Terms of Service</Link>
                      {" "}and{" "}
                      <Link to="/privacy" className="font-medium text-gray-800 underline">Privacy Policy</Link>.
                    </span>
                  </label>
                )}
                <button
                  type="submit"
                  disabled={loading || !email || (isDoctorSurface && !acceptedTerms)}
                  className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? "Sending…" : "Continue"}
                </button>
                {/* Clerk Smart CAPTCHA mounts here during sign-up. Without this
                    element Clerk falls back to a flaky invisible widget that
                    triggers a Cloudflare challenge and makes sign-in unreliable. */}
                <div id="clerk-captcha" className="empty:hidden" />
              </form>

              <div className="mt-5 flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">or</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading || (isDoctorSurface && !acceptedTerms)}
                className="mt-4 w-full flex items-center justify-center gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </>
          ) : (
            <>
              <div className="mb-7 text-center">
                <h1 className="text-xl font-semibold text-gray-900">Check your email</h1>
                <p className="mt-1.5 text-sm text-gray-500">
                  We sent a 6-digit code to <span className="font-medium text-gray-700">{email}</span>
                </p>
              </div>
              <form onSubmit={handleCodeSubmit} className="space-y-4">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Verification code
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    autoFocus
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-center text-lg tracking-[0.3em] text-gray-900 placeholder-gray-300 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  disabled={loading || code.length < 6}
                  className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? "Verifying…" : "Verify"}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("email"); setCode(""); setError(null) }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Use a different email
                </button>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  )
}
